import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Plus,
  PencilSimple,
  ShieldCheck,
  ArrowLeft,
  Check,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import api from "../lib/api";
import { useAuth } from "@/context/AuthContext";
import { PERMISSIONS, PERMISSION_INFO } from "@/lib/permissions";

const DOMAIN_ORDER = ["Sistema", "Personas", "Finanzas", "Social"];

const FALLBACK_PERMISSION_INFO = {
  label: "Permiso",
  description: "",
  domain: "Sistema",
};

const getPermissionInfo = (permission) => {
  return (
    PERMISSION_INFO[permission] || {
      ...FALLBACK_PERMISSION_INFO,
      label: permission,
    }
  );
};

const getPermissionLabel = (permission) => {
  return getPermissionInfo(permission).label;
};

const getPermissionDescription = (permission) => {
  return getPermissionInfo(permission).description;
};

const getPermissionDomain = (permission) => {
  return getPermissionInfo(permission).domain;
};

const getRoleColor = (roleId) => {
  const colors = [
    "bg-[#FFE4E6] text-[#BE123C]",
    "bg-[#FFEDD5] text-[#9A3412]",
    "bg-[#FEF3C7] text-[#92400E]",
    "bg-[#DCFCE7] text-[#166534]",
    "bg-[#DBEAFE] text-[#1E40AF]",
    "bg-[#E0E7FF] text-[#3730A3]",
    "bg-[#F3E8FF] text-[#7E22CE]",
    "bg-[#FCE7F3] text-[#9D174D]",
  ];

  let hash = 0;

  for (let i = 0; i < roleId.length; i++) {
    hash = (hash << 5) - hash + roleId.charCodeAt(i);
    hash |= 0;
  }

  return colors[Math.abs(hash) % colors.length];
};

/*
 * ============================================================
 * SCOPES
 * ============================================================
 *
 * Solo estos permisos tienen actualmente un ámbito configurable.
 *
 * Si el array de categorías está vacío o el scope no existe,
 * significa que el permiso no tiene restricción y aplica a
 * todas las categorías.
 */
const SCOPE_CONFIG = {
  [PERMISSIONS.MANAGE_EVENTOS]: {
    scopeType: "eventos",
    label: "Categorías de eventos",
    description: "Limita qué categorías de eventos puede gestionar este rol.",
    options: [
      { value: "festejos", label: "Festejos" },
      { value: "bar", label: "Bar" },
      { value: "junta", label: "Junta" },
      { value: "otro", label: "Otro" },
    ],
  },

  [PERMISSIONS.MANAGE_ASISTENCIA]: {
    scopeType: "asistencia",
    label: "Categorías de asistencia",
    description:
      "Limita en qué categorías de eventos puede gestionar la asistencia este rol.",
    options: [
      { value: "festejos", label: "Festejos" },
      { value: "bar", label: "Bar" },
      { value: "junta", label: "Junta" },
      { value: "otro", label: "Otro" },
    ],
  },
};

export default function Roles() {
  const { user: me, can } = useAuth();
  const navigate = useNavigate();

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);

  const [modal, setModal] = useState(null);

  const [form, setForm] = useState({
    id: "",
    name: "",
    permissions: [],
    scopes: {},
  });

  const [saving, setSaving] = useState(false);

  const permissionCatalog = useMemo(() => {
    return Object.values(PERMISSIONS);
  }, []);

  const permissionsByDomain = useMemo(() => {
    const grouped = {};

    permissionCatalog.forEach((permission) => {
      const domain = getPermissionDomain(permission);

      if (!grouped[domain]) {
        grouped[domain] = [];
      }

      grouped[domain].push(permission);
    });

    return Object.entries(grouped).sort(([domainA], [domainB]) => {
      const indexA = DOMAIN_ORDER.indexOf(domainA);
      const indexB = DOMAIN_ORDER.indexOf(domainB);

      if (indexA === -1 && indexB === -1) {
        return domainA.localeCompare(domainB);
      }

      if (indexA === -1) return 1;
      if (indexB === -1) return -1;

      return indexA - indexB;
    });
  }, [permissionCatalog]);

  const scopedPermissions = useMemo(() => {
    return Object.keys(SCOPE_CONFIG);
  }, []);

  const fetchRoles = async () => {
    setLoading(true);

    try {
      const { data } = await api.get("/roles");
      setRoles(data || []);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "No se han podido cargar los roles",
      );
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!me || !can(PERMISSIONS.MANAGE_ROLES)) {
      return;
    }

    fetchRoles();
  }, [me]);

  if (me && !can(PERMISSIONS.MANAGE_ROLES)) {
    return (
      <div className="p-6 md:p-10">
        <div className="max-w-3xl mx-auto bg-med-surface border border-med-border rounded-2xl p-8 text-center">
          <ShieldCheck
            size={48}
            weight="duotone"
            className="mx-auto mb-4 text-med-muted"
          />

          <h2 className="text-xl font-bold text-med-text">
            Acceso restringido
          </h2>

          <p className="mt-2 text-med-muted">
            No tienes permisos para gestionar roles.
          </p>
        </div>
      </div>
    );
  }

  const openCreateModal = () => {
    setForm({
      id: "",
      name: "",
      permissions: [],
      scopes: {},
    });

    setModal({
      mode: "create",
      data: null,
    });
  };

  const openEditModal = (role) => {
    setForm({
      id: role.id || "",
      name: role.name || "",
      permissions: Array.isArray(role.permissions) ? [...role.permissions] : [],
      scopes: role.scopes ? structuredClone(role.scopes) : {},
    });

    setModal({
      mode: "edit",
      data: role,
    });
  };

  const closeModal = () => {
    if (saving) return;

    setModal(null);

    setForm({
      id: "",
      name: "",
      permissions: [],
      scopes: {},
    });
  };

  const togglePermission = (permission) => {
    setForm((current) => {
      const permissions = current.permissions || [];

      if (permissions.includes(permission)) {
        const nextScopes = { ...(current.scopes || {}) };

        const scopeConfig = SCOPE_CONFIG[permission];

        if (scopeConfig) {
          delete nextScopes[scopeConfig.scopeType];
        }

        return {
          ...current,
          permissions: permissions.filter((item) => item !== permission),
          scopes: nextScopes,
        };
      }

      return {
        ...current,
        permissions: [...permissions, permission],
      };
    });
  };

  const toggleAllPermissions = () => {
    setForm((current) => {
      const permissions = current.permissions || [];

      if (permissions.includes("*")) {
        return {
          ...current,
          permissions: [],
          scopes: {},
        };
      }

      return {
        ...current,
        permissions: ["*"],
        scopes: {},
      };
    });
  };

  const toggleDomain = (domainPermissions) => {
    setForm((current) => {
      if (current.permissions.includes("*")) {
        return {
          ...current,
          permissions: [],
          scopes: {},
        };
      }

      const currentPermissions = current.permissions || [];

      const allSelected = domainPermissions.every((permission) =>
        currentPermissions.includes(permission),
      );

      if (allSelected) {
        const nextPermissions = currentPermissions.filter(
          (permission) => !domainPermissions.includes(permission),
        );

        const nextScopes = { ...(current.scopes || {}) };

        domainPermissions.forEach((permission) => {
          const scopeConfig = SCOPE_CONFIG[permission];

          if (scopeConfig) {
            delete nextScopes[scopeConfig.scopeType];
          }
        });

        return {
          ...current,
          permissions: nextPermissions,
          scopes: nextScopes,
        };
      }

      const merged = [
        ...currentPermissions,
        ...domainPermissions.filter(
          (permission) => !currentPermissions.includes(permission),
        ),
      ];

      return {
        ...current,
        permissions: merged,
      };
    });
  };

  const toggleScopeValue = (permission, value) => {
    const scopeConfig = SCOPE_CONFIG[permission];

    if (!scopeConfig) return;

    const scopeType = scopeConfig.scopeType;

    setForm((current) => {
      const currentScopes = { ...(current.scopes || {}) };
      const currentValues = Array.isArray(currentScopes[scopeType])
        ? currentScopes[scopeType]
        : [];

      const nextValues = currentValues.includes(value)
        ? currentValues.filter((item) => item !== value)
        : [...currentValues, value];

      /*
       * Si no hay ninguna categoría seleccionada,
       * eliminamos el scope.
       *
       * Esto significa "todas las categorías".
       */
      if (nextValues.length === 0) {
        delete currentScopes[scopeType];
      } else {
        currentScopes[scopeType] = nextValues;
      }

      return {
        ...current,
        scopes: currentScopes,
      };
    });
  };

  const clearScope = (permission) => {
    const scopeConfig = SCOPE_CONFIG[permission];

    if (!scopeConfig) return;

    setForm((current) => {
      const nextScopes = { ...(current.scopes || {}) };

      delete nextScopes[scopeConfig.scopeType];

      return {
        ...current,
        scopes: nextScopes,
      };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const id = form.id.trim();
    const name = form.name.trim();

    if (!id) {
      toast.error("El identificador del rol es obligatorio");
      return;
    }

    if (!name) {
      toast.error("El nombre del rol es obligatorio");
      return;
    }

    if (!/^[a-z0-9_-]+$/.test(id)) {
      toast.error(
        "El identificador solo puede contener minúsculas, números, guiones y guiones bajos",
      );
      return;
    }

    setSaving(true);

    try {
      /*
       * Limpiamos scopes:
       * - solo conservamos scopes de permisos seleccionados
       * - eliminamos scopes vacíos
       * - "*" nunca necesita scopes
       */
      const cleanedScopes = {};

      if (!form.permissions.includes("*")) {
        scopedPermissions.forEach((permission) => {
          if (!form.permissions.includes(permission)) {
            return;
          }

          const scopeConfig = SCOPE_CONFIG[permission];
          const values = form.scopes?.[scopeConfig.scopeType];

          if (Array.isArray(values) && values.length > 0) {
            cleanedScopes[scopeConfig.scopeType] = values;
          }
        });
      }

      if (modal.mode === "create") {
        await api.post("/roles", {
          id,
          name,
          permissions: form.permissions,
          scopes: cleanedScopes,
        });

        toast.success("Rol creado correctamente");
      } else {
        await api.put(`/roles/${modal.data.id}`, {
          name,
          permissions: form.permissions,
          scopes: cleanedScopes,
        });

        toast.success("Rol actualizado correctamente");
      }

      closeModal();
      await fetchRoles();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "No se ha podido guardar el rol",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 md:p-10">
      <button
        type="button"
        onClick={() => navigate("/usuarios")}
        className="inline-flex items-center gap-1.5 text-sm text-med-muted hover:text-med-text mb-6"
      >
        <ArrowLeft size={14} weight="bold" />
        Volver a usuarios
      </button>

      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-med-muted font-semibold mb-2">
            Administración
          </div>

          <h1 className="font-display text-4xl font-bold text-med-text">
            Roles
          </h1>

          <p className="text-med-muted mt-1">
            Gestiona los roles y los permisos de la falla.
          </p>
        </div>

        <button
          type="button"
          onClick={openCreateModal}
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-med-primary px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-med-primary/90"
        >
          <Plus size={20} weight="bold" />
          Nuevo rol
        </button>
      </header>

      {/* Roles */}
      {loading ? (
        <div className="bg-med-surface border border-med-border rounded-2xl p-8 text-center text-med-muted">
          Cargando roles...
        </div>
      ) : roles.length === 0 ? (
        <div className="bg-med-surface border border-med-border rounded-2xl p-10 text-center">
          <ShieldCheck
            size={48}
            weight="duotone"
            className="mx-auto mb-4 text-med-muted"
          />

          <h2 className="text-lg font-semibold text-med-text">
            No hay roles configurados
          </h2>

          <p className="text-sm text-med-muted mt-1 mb-5">
            Crea el primer rol para configurar los permisos.
          </p>

          <button
            type="button"
            onClick={openCreateModal}
            className="inline-flex items-center gap-2 bg-med-primary hover:bg-med-primary-hover text-white rounded-xl px-5 py-2.5 font-semibold"
          >
            <Plus size={18} weight="bold" />
            Crear rol
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {roles.map((role) => {
            const hasAllPermissions =
              Array.isArray(role.permissions) && role.permissions.includes("*");

            const permissionCount = hasAllPermissions
              ? permissionCatalog.length
              : Array.isArray(role.permissions)
                ? role.permissions.length
                : 0;

            const hasScopes =
              !hasAllPermissions &&
              role.scopes &&
              Object.values(role.scopes).some(
                (values) => Array.isArray(values) && values.length > 0,
              );

            return (
              <motion.div
                key={role.id}
                layout
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-3xl border border-med-border bg-white p-6 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-11 h-11 shrink-0 rounded-2xl flex items-center justify-center ${getRoleColor(
                          role.id,
                        )}`}
                      >
                        <ShieldCheck size={22} weight="duotone" />
                      </div>

                      <div className="min-w-0">
                        <h2 className="font-display text-lg font-bold text-med-text truncate">
                          {role.name}
                        </h2>

                        <span className="text-xs text-med-muted">
                          {role.id}
                        </span>
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => openEditModal(role)}
                    className="p-2 rounded-xl text-med-text hover:bg-med-surface2 shrink-0"
                    title="Editar rol"
                  >
                    <PencilSimple size={18} />
                  </button>
                </div>

                <div className="mt-6 pt-4 border-t border-med-border">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-sm">
                      <ShieldCheck
                        size={18}
                        weight="duotone"
                        className="text-med-muted"
                      />

                      <span className="text-med-text font-medium">
                        {hasAllPermissions
                          ? "Todos los permisos"
                          : `${permissionCount} ${
                              permissionCount === 1 ? "permiso" : "permisos"
                            }`}
                      </span>
                    </div>
                  </div>

                  {!hasAllPermissions && permissionCount > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {role.permissions.slice(0, 5).map((permission) => (
                        <span
                          key={permission}
                          className="px-2 py-1 rounded-md bg-med-surface2 text-med-muted text-xs"
                        >
                          {getPermissionLabel(permission)}
                        </span>
                      ))}

                      {permissionCount > 5 && (
                        <span className="px-2 py-1 rounded-md bg-med-surface2 text-med-muted text-xs">
                          +{permissionCount - 5}
                        </span>
                      )}
                    </div>
                  )}

                  {hasScopes && (
                    <div className="mt-3 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-med-primary/5 text-med-primary text-xs font-medium">
                      <span>Con restricciones de ámbito</span>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={closeModal} />

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden bg-med-surface border border-med-border rounded-2xl shadow-xl"
          >
            <form
              onSubmit={handleSubmit}
              className="flex flex-col max-h-[90vh]"
            >
              {/* Modal header */}
              <div className="flex items-center justify-between gap-4 px-6 py-5 border-b border-med-border">
                <div>
                  <h2 className="text-xl font-bold text-med-text">
                    {modal.mode === "create" ? "Nuevo rol" : "Editar rol"}
                  </h2>

                  <p className="text-sm text-med-muted mt-1">
                    Configura el nombre, los permisos y los ámbitos del rol.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={closeModal}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-lg hover:bg-med-surface2 text-med-muted"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Modal body */}
              <div className="overflow-y-auto p-6 space-y-6">
                {/* Basic info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-med-text mb-2">
                      Identificador
                    </label>

                    <input
                      type="text"
                      value={form.id}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          id: event.target.value.toLowerCase(),
                        }))
                      }
                      disabled={modal.mode === "edit"}
                      placeholder="ej. tesorero"
                      className="w-full rounded-xl border border-med-border bg-med-surface2 px-3 py-2.5 text-med-text outline-none focus:ring-2 focus:ring-med-primary/30 disabled:opacity-60"
                    />

                    <p className="text-xs text-med-muted mt-1.5">
                      Solo minúsculas, números, guiones y guiones bajos.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-med-text mb-2">
                      Nombre
                    </label>

                    <input
                      type="text"
                      value={form.name}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          name: event.target.value,
                        }))
                      }
                      placeholder="ej. Tesorero"
                      className="w-full rounded-xl border border-med-border bg-med-surface2 px-3 py-2.5 text-med-text outline-none focus:ring-2 focus:ring-med-primary/30"
                    />
                  </div>
                </div>

                {/* Permissions */}
                <div>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                    <div>
                      <h3 className="text-base font-bold text-med-text">
                        Permisos
                      </h3>

                      <p className="text-sm text-med-muted mt-1">
                        Selecciona las acciones que podrá realizar este rol.
                      </p>
                    </div>

                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.permissions.includes("*")}
                        onChange={toggleAllPermissions}
                        className="w-4 h-4 rounded border-med-border"
                      />

                      <span className="text-sm font-semibold text-med-text">
                        Todos los permisos
                      </span>
                    </label>
                  </div>

                  {form.permissions.includes("*") && (
                    <div className="mb-4 rounded-xl border border-med-border bg-med-surface2 p-4">
                      <div className="flex items-center gap-2 text-sm text-med-text">
                        <Check
                          size={18}
                          weight="bold"
                          className="text-med-primary"
                        />

                        <span>
                          Este rol tendrá acceso a todos los permisos
                          disponibles y no tendrá restricciones de ámbito.
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="space-y-5">
                    {permissionsByDomain.map(([domain, domainPermissions]) => {
                      const allSelected =
                        form.permissions.includes("*") ||
                        domainPermissions.every((permission) =>
                          form.permissions.includes(permission),
                        );

                      return (
                        <div
                          key={domain}
                          className="border border-med-border rounded-2xl overflow-hidden"
                        >
                          <div className="flex items-center justify-between gap-3 px-4 py-3 bg-med-surface2 border-b border-med-border">
                            <h4 className="font-semibold text-med-text">
                              {domain}
                            </h4>

                            <button
                              type="button"
                              onClick={() => toggleDomain(domainPermissions)}
                              className="text-xs font-semibold text-med-text hover:underline"
                            >
                              {allSelected
                                ? "Quitar todos"
                                : "Seleccionar todos"}
                            </button>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-4">
                            {domainPermissions.map((permission) => {
                              const selected =
                                form.permissions.includes("*") ||
                                form.permissions.includes(permission);

                              return (
                                <div key={permission}>
                                  <label
                                    className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition ${
                                      selected
                                        ? "border-med-primary/40 bg-med-primary/5"
                                        : "border-med-border hover:bg-med-surface2"
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selected}
                                      disabled={form.permissions.includes("*")}
                                      onChange={() =>
                                        togglePermission(permission)
                                      }
                                      className="mt-0.5 w-4 h-4 rounded border-med-border"
                                    />

                                    <span className="min-w-0">
                                      <span className="block text-sm font-semibold text-med-text">
                                        {getPermissionLabel(permission)}
                                      </span>

                                      {getPermissionDescription(permission) && (
                                        <span className="block text-xs text-med-muted mt-1">
                                          {getPermissionDescription(permission)}
                                        </span>
                                      )}
                                    </span>
                                  </label>

                                  {/* Scope */}
                                  {selected &&
                                    !form.permissions.includes("*") &&
                                    SCOPE_CONFIG[permission] && (
                                      <div className="mt-2 ml-7 rounded-xl border border-med-border bg-med-surface2/50 p-4">
                                        <div className="flex flex-col gap-1 mb-3">
                                          <div className="flex items-center justify-between gap-3">
                                            <span className="text-sm font-semibold text-med-text">
                                              {SCOPE_CONFIG[permission].label}
                                            </span>

                                            {form.scopes?.[
                                              SCOPE_CONFIG[permission].scopeType
                                            ]?.length > 0 && (
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  clearScope(permission)
                                                }
                                                className="text-xs font-medium text-med-muted hover:text-med-text hover:underline"
                                              >
                                                Todas
                                              </button>
                                            )}
                                          </div>

                                          <p className="text-xs text-med-muted">
                                            {
                                              SCOPE_CONFIG[permission]
                                                .description
                                            }
                                          </p>
                                        </div>

                                        <div className="grid grid-cols-2 gap-2">
                                          {SCOPE_CONFIG[permission].options.map(
                                            (option) => {
                                              const scopeType =
                                                SCOPE_CONFIG[permission]
                                                  .scopeType;

                                              const selectedValues =
                                                form.scopes?.[scopeType] || [];

                                              const checked =
                                                selectedValues.includes(
                                                  option.value,
                                                );

                                              return (
                                                <label
                                                  key={option.value}
                                                  className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer transition ${
                                                    checked
                                                      ? "border-med-primary/40 bg-med-primary/5"
                                                      : "border-med-border hover:bg-med-surface"
                                                  }`}
                                                >
                                                  <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() =>
                                                      toggleScopeValue(
                                                        permission,
                                                        option.value,
                                                      )
                                                    }
                                                    className="w-4 h-4 rounded border-med-border"
                                                  />

                                                  <span className="text-sm text-med-text">
                                                    {option.label}
                                                  </span>
                                                </label>
                                              );
                                            },
                                          )}
                                        </div>

                                        <p className="text-[11px] text-med-muted mt-3">
                                          Si no seleccionas ninguna categoría,
                                          el permiso se aplica a todas.
                                        </p>
                                      </div>
                                    )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Modal footer */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-med-border">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={saving}
                  className="px-4 py-2.5 rounded-xl border border-med-border bg-med-surface hover:bg-med-surface2 text-med-text font-semibold disabled:opacity-50"
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-med-primary hover:bg-med-primary-hover text-white font-semibold disabled:opacity-50"
                >
                  {saving
                    ? "Guardando..."
                    : modal.mode === "create"
                      ? "Crear rol"
                      : "Guardar cambios"}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
